import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ALL_DENIED,
  ALL_GRANTED,
  type ConsentCategories,
  type ConsentState,
  type Jurisdiction,
} from './types';
import { POLICY_VERSION } from './policy-version';

const STORAGE_KEY = 'letsloop-consent-v1';
const JURISDICTION_CACHE_KEY = 'letsloop-jurisdiction-v1';
const JURISDICTION_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
// Safe default until /api/geo resolves: UK is the strictest regime we serve.
const DEFAULT_JURISDICTION: Jurisdiction = 'UK';

interface CachedJurisdiction {
  value: Jurisdiction;
  fetchedAt: number;
}

function readCachedJurisdiction(): Jurisdiction | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(JURISDICTION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedJurisdiction;
    if (Date.now() - parsed.fetchedAt > JURISDICTION_CACHE_TTL_MS) return null;
    if (parsed.value !== 'UK' && parsed.value !== 'EU' && parsed.value !== 'OTHER') {
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

function writeCachedJurisdiction(value: Jurisdiction): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      JURISDICTION_CACHE_KEY,
      JSON.stringify({ value, fetchedAt: Date.now() } satisfies CachedJurisdiction),
    );
  } catch {
    // storage unavailable — fall back to re-fetch next session
  }
}

async function fetchJurisdiction(signal: AbortSignal): Promise<Jurisdiction> {
  try {
    const res = await fetch('/api/geo', { signal });
    if (!res.ok) return DEFAULT_JURISDICTION;
    const data = (await res.json()) as { jurisdiction?: unknown };
    const j = data?.jurisdiction;
    if (j === 'UK' || j === 'EU' || j === 'OTHER') return j;
    return DEFAULT_JURISDICTION;
  } catch {
    return DEFAULT_JURISDICTION;
  }
}

interface ConsentContextValue {
  state: ConsentState | null;
  isDecided: boolean;
  /** False until the stored decision has been read on the client. */
  isHydrated: boolean;
  isPreferencesOpen: boolean;
  accept: (categories: Partial<ConsentCategories>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  reset: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function readStoredState(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.policyVersion !== POLICY_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function logConsent(state: ConsentState): void {
  try {
    void fetch('/api/consent-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(state),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // fire-and-forget
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConsentState | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>(
    () => readCachedJurisdiction() ?? DEFAULT_JURISDICTION,
  );

  useEffect(() => {
    setState(readStoredState());
    setIsHydrated(true);
  }, []);

  // Resolve jurisdiction from the server's x-vercel-ip-country header.
  // Cached for 24h in localStorage. Safe on SSR (guarded) and aborts on unmount.
  useEffect(() => {
    if (readCachedJurisdiction() !== null) return;
    const controller = new AbortController();
    void fetchJurisdiction(controller.signal).then((j) => {
      if (controller.signal.aborted) return;
      setJurisdiction(j);
      writeCachedJurisdiction(j);
    });
    return () => controller.abort();
  }, []);

  const persist = useCallback((next: ConsentState) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // storage unavailable
    }
    setState(next);
    logConsent(next);
  }, []);

  const accept = useCallback(
    (partial: Partial<ConsentCategories>) => {
      const categories: ConsentCategories = {
        ...ALL_DENIED,
        ...partial,
        strictly_necessary: true,
      };
      persist({
        categories,
        timestamp: Date.now(),
        policyVersion: POLICY_VERSION,
        jurisdiction,
      });
    },
    [persist, jurisdiction],
  );

  const acceptAll = useCallback(() => {
    persist({
      categories: ALL_GRANTED,
      timestamp: Date.now(),
      policyVersion: POLICY_VERSION,
      jurisdiction,
    });
  }, [persist, jurisdiction]);

  const rejectAll = useCallback(() => {
    persist({
      categories: ALL_DENIED,
      timestamp: Date.now(),
      policyVersion: POLICY_VERSION,
      jurisdiction,
    });
  }, [persist, jurisdiction]);

  const reset = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable
    }
    setState(null);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      isDecided: isHydrated && state !== null,
      isHydrated,
      isPreferencesOpen,
      accept,
      acceptAll,
      rejectAll,
      reset,
      openPreferences: () => setIsPreferencesOpen(true),
      closePreferences: () => setIsPreferencesOpen(false),
    }),
    [state, isHydrated, isPreferencesOpen, accept, acceptAll, rejectAll, reset],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
}
